import React, { useState } from 'react';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Chart from 'react-apexcharts';
import moment from 'moment';

import TabbedNav from '@buyside/components/presentation/TabbedNav/tabbedNav';

import { formatCategory } from '@buyside/util/rankCategoryHelper';

import { Maybe, useUserProfileRankingChartQuery } from '@interfaces/graphql-typings';

import styles from './userRankingLineChartStyles.scss';

export const timeframes = [
  'Last 12 Months',
  'Last 24 Months',
  'Last 36 Months',
  'Last 5 Years',
  'All'
];

const UserRankingLineChart: React.FC<{
  profileId: string;
}> = ({ profileId }) => {
  const [timeframe, setTimeframe] = useState(timeframes[0]);

  const onTimeframeChange = (val: string): void => setTimeframe(val);

  const { data, loading, error } = useUserProfileRankingChartQuery({
    variables: { profileId, timeframe }
  });

  if (error) throw error;

  const ranks = data?.userProfile?.user?.publicRankings;
  if (!ranks || !ranks.length) return null;

  const generateSeries = (): {
    name: string;
    data: {
      x: number;
      y: Maybe<number>;
    }[];
  }[] => {
    // below creates an object with each key being a ranking category and the value being an array of
    // objects (ranks) with the 'x' value = to the month the rank was created in ms and 'y' value = to
    // either the rank value or null  M.O.M. 2/9/2023
    const categoryObj =
      ranks.reduce((result: { [key: string]: { x: number; y: number }[] }, rank) => {
        if (!rank) return result;

        const rankCategory = rank.category;
        const rankValue = (rank.value || 0) + 1;
        // setting the date to the beginning of the month the rank was created
        const rankDate = moment(rank.createdAt)
          .startOf('month')
          .toDate()
          .getTime();

        const rankingCategorySeries = result[rankCategory] || (result[rankCategory] = []);

        rankingCategorySeries.push({
          x: rankDate,
          y: rankValue
        });

        // sorting the categories ranks array by createdAt (x = createdAt at the beginning of the month in ms)
        rankingCategorySeries.sort((a, b) => a.x - b.x);

        return result;
      }, {}) || {};

    // we are then creating an array of objects with two keys, 'name' being the formatted category
    // name and 'data' being the categories ranks ordered by createdAt (x)
    return Object.keys(categoryObj).map(category => {
      return {
        name: formatCategory(category),
        data: categoryObj[category]
      };
    });
  };

  return (
    <>
      <div className={styles.actionArea}>
        <h4>Historical Rankings Chart</h4>
        {loading && <FontAwesomeIcon icon={faSpinner} spin />}
        <TabbedNav
          initialTabIndex={timeframes.findIndex(tab => tab === timeframe)}
          onChange={onTimeframeChange}
          tabs={timeframes}
        />
      </div>
      <Chart
        data-testid="ranking-line-chart"
        type="line"
        height={450}
        options={{
          chart: {
            fontFamily: 'Lato',
            animations: {
              animateGradually: { enabled: false }
            },
            zoom: {
              allowMouseWheelZoom: false
            }
          },
          legend: {
            showForSingleSeries: true,
            itemMargin: {
              horizontal: 10,
              vertical: 10
            }
          },
          markers: {
            size: 4
          },
          stroke: {
            curve: 'smooth',
            width: 2
          },
          tooltip: {
            shared: false,
            intersect: true,
            x: {
              format: 'MMMM, yyyy'
            },
            y: {
              formatter: value => `Ranked at #${value}`
            }
          },
          xaxis: {
            type: 'datetime',
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
              format: 'MMM, yyyy',
              minHeight: 45,
              showDuplicates: false
            }
          },
          yaxis: {
            reversed: true,
            min: 1,
            labels: {
              formatter: val => val.toFixed()
            }
          }
        }}
        series={generateSeries()}
      />
    </>
  );
};

export default UserRankingLineChart;
